import { Menu, Transition } from "@headlessui/react";
import React from "react";
import styled from "styled-components";

export interface ProfileDropdownMenuItem {
  content: React.ReactNode;
  selected?: boolean;
  disabled?: boolean;
  action: React.MouseEventHandler<HTMLButtonElement>;
}

const DropdownRoot = styled.div`
  position: relative;
  z-index: 50;
  display: inline-block;
  width: 100%;
  text-align: left;

  > .profile-dropdown-trigger {
    display: flex;
    width: 100%;
    padding: 14px 16px;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
    border-radius: 8px;
    background: ${({ theme }) => theme.globalConstants.color.white};
    cursor: pointer;
    text-align: left;
  }

  > .profile-dropdown-trigger[aria-invalid="true"] {
    border-color: ${({ theme }) => theme.globalConstants.color.borderDanger};
  }

  > .profile-dropdown-trigger:focus-visible {
    outline: 2px solid ${({ theme }) => theme.globalConstants.color.borderFocus};
  }
`;

const DropdownItems = styled(Menu.Items)`
  position: absolute;
  left: 0;
  z-index: 50;
  width: 100%;
  margin-top: 0;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 8px;
  background: ${({ theme }) => theme.globalConstants.color.white};
  box-shadow: 0 4px 12px rgb(0 0 0 / 10%);
  outline: none;
`;

const DropdownSection = styled.div`
  padding: 4px 0;
`;

const DropdownItem = styled.button<{
  $active: boolean;
  $selected: boolean;
}>`
  display: flex;
  width: 100%;
  padding: 8px 16px;
  align-items: center;
  justify-content: space-between;
  border: 0;
  background: ${({ $active, $selected, theme }) =>
    $active
      ? theme.globalConstants.color.backgroundPrimary
      : $selected
      ? theme.globalConstants.color.backgroundPrimaryLight
      : theme.globalConstants.color.white};
  color: ${({ $active, theme }) =>
    $active
      ? theme.globalConstants.color.white
      : theme.globalConstants.color.textBody};
  cursor: pointer;
  font: inherit;
  text-align: left;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const ProfileDropdownMenu: React.FC<{
  children: React.ReactNode;
  items: ProfileDropdownMenuItem[][];
  hasError?: boolean;
  describedBy?: string;
}> = ({ children, items, hasError, describedBy }) => (
  <Menu as={DropdownRoot}>
    <Menu.Button
      as="button"
      className="profile-dropdown-trigger"
      type="button"
      aria-invalid={hasError}
      aria-describedby={describedBy}
    >
      {children}
    </Menu.Button>
    <Transition
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <DropdownItems>
        {items.map((section, sectionIndex) => (
          <DropdownSection key={sectionIndex}>
            {section.map((item, itemIndex) => (
              <Menu.Item key={itemIndex} disabled={item.disabled}>
                {({ active }) => (
                  <DropdownItem
                    type="button"
                    $active={active}
                    $selected={item.selected ?? false}
                    disabled={item.disabled}
                    onClick={item.action}
                  >
                    {item.content}
                  </DropdownItem>
                )}
              </Menu.Item>
            ))}
          </DropdownSection>
        ))}
      </DropdownItems>
    </Transition>
  </Menu>
);

export default ProfileDropdownMenu;
